const merge = (source, dest) => {
  Object.keys(dest).forEach((i) => {
    Object.keys(dest[i].categories).forEach((j) => {
      if (source[i] && source[i].categories[j]) {
        source[i].categories[j].y += dest[i].categories[j].y;
        source[i].y += dest[i].categories[j].y;
      } else if (source[i]) {
        source[i].categories[j] = dest[i].categories[j];
        source[i].y += dest[i].categories[j].y;
      }
    });

    if (source[i] === undefined) {
      source[i] = dest[i];
    }
  });

  return source;
};

export const calculateRate = (clicks, denominator, unavailable = false) =>
  !unavailable && denominator > 0 ? (clicks / denominator) * 100 : null;

export const formatRate = (value, digits = 1) =>
  Number.isFinite(value) ? `${value.toLocaleString(undefined, { maximumFractionDigits: digits })}%` : 'N/A';

export const formatExportRate = (value) => (Number.isFinite(value) ? `${value.toFixed(2)}%` : 'N/A');

export const sumMetricSeries = (...series) => {
  const points = new Map();
  series.flat().forEach(([time, value]) => points.set(time, (points.get(time) || 0) + value));
  return [...points].sort(([a], [b]) => a - b);
};

// Basic clicks have no recipient attribution. Keep them out of recipient-based rates.
const metricRates = (totals, unavailable, basicUnavailable) => ({
  ctr: calculateRate(totals.adclick, totals.adview, unavailable),
  ctrr: calculateRate(totals.adclick, totals.uniques),
  combinedCtr: calculateRate(totals.combinedClicks, totals.combinedViews, unavailable),
  combinedCtrr: calculateRate(totals.adclick, totals.uniques),
  basicCtr: calculateRate(totals.basicadclick, totals.basicadview, basicUnavailable)
});

export const summarizeMetrics = (reports) => {
  const totals = {
    ad: 0,
    adview: 0,
    adclick: 0,
    basicadview: 0,
    basicadclick: 0,
    uniques: 0,
    combinedViews: 0,
    combinedClicks: 0
  };
  reports.forEach((report) =>
    Object.keys(totals).forEach((key) => {
      totals[key] += report.disableviews && (key === 'adview' || key === 'basicadview') ? 0 : report.totals[key] || 0;
    })
  );
  const unavailable = reports.some((report) => report.ctrUnavailable && report.totals.combinedClicks > 0);
  const basicUnavailable = reports.some((report) => report.ctrUnavailable && report.totals.basicadclick > 0);
  return { ...totals, ...metricRates(totals, unavailable, basicUnavailable) };
};

export const consolidateMetrics = (results, campaign) => {
  const disabledViews = campaign && campaign.disableviews;

  const report = {
    uniques: {},
    locations: {},
    totals: {
      ad: 0,
      adclick: 0,
      adview: 0,
      basicadclick: 0,
      basicadview: 0,
      dayslive: 0,
      uniques: 0
    },
    deliveries: [],
    views: [],
    clicks: [],
    basicviews: [],
    basicclicks: [],
    devicedata: {
      adview: {},
      adclick: {}
    },
    applicationdata: {
      adview: {},
      adclick: {}
    },
    calculated: {
      delivered: 0,
      views: 0,
      clicks: 0,
      reach: 0
    }
  };

  const timeslices = {};
  let sawUniques = false;

  (results || []).forEach((data) => {
    report.totals.ad += data.deliveries || 0;
    report.totals.adclick += data.clicks || 0;
    report.totals.basicadclick += data.basicclicks || 0;
    report.totals.adview += data.views || 0;
    report.totals.basicadview += data.basicviews || 0;

    const timestamp = new Date(data.timeslice).getTime();

    if (data.deliveries || data.views || data.clicks || data.basicviews || data.basicclicks) {
      report.totals.dayslive++;
    }

    if (data.countries) {
      Object.keys(data.countries).forEach((country) => {
        report.locations[country] = report.locations[country] || 0;
        report.locations[country] += data.countries[country];
      });
    }

    if (data.adviewos) {
      report.devicedata.adview = merge(report.devicedata.adview, data.adviewos);
    }

    if (data.adclickos) {
      report.devicedata.adclick = merge(report.devicedata.adclick, data.adclickos);
    }

    if (data.adviewua) {
      report.applicationdata.adview = merge(report.applicationdata.adview, data.adviewua);
    }

    if (data.adclickua) {
      report.applicationdata.adclick = merge(report.applicationdata.adclick, data.adclickua);
    }

    timeslices[timestamp] = timeslices[timestamp] || {
      deliveries: 0,
      views: 0,
      clicks: 0,
      basicviews: 0,
      basicclicks: 0
    };

    timeslices[timestamp].deliveries += data.deliveries || 0;
    timeslices[timestamp].clicks += data.clicks || 0;
    timeslices[timestamp].basicclicks += data.basicclicks || 0;
    timeslices[timestamp].views += data.views || 0;
    timeslices[timestamp].basicviews += data.basicviews || 0;

    if (data.uniques) {
      sawUniques = true;

      data.uniques.forEach((unique) => {
        if (!unique.endsWith('@bcc.hubspot.com')) {
          report.uniques[unique] = report.uniques[unique] || 0;
          report.uniques[unique]++;
        }
      });
    }
  });

  // Counted once, after every day has been folded in. Doing this inside the loop above
  // re-enumerated a report.uniques that grows to the domain's entire recipient list —
  // O(days x recipients) — and left reach reflecting only the final day.
  report.totals.uniques = sawUniques ? Object.keys(report.uniques).length : 0;

  Object.keys(timeslices).forEach((ts) => {
    const timestamp = parseInt(ts);
    report.deliveries.push([timestamp, timeslices[timestamp].deliveries || 0]);
    report.clicks.push([timestamp, timeslices[timestamp].clicks || 0]);
    report.basicclicks.push([timestamp, timeslices[timestamp].basicclicks || 0]);
    report.views.push([timestamp, timeslices[timestamp].views || 0]);
    report.basicviews.push([timestamp, timeslices[timestamp].basicviews || 0]);
  });

  report.disableviews = !!disabledViews;
  report.ctrUnavailable = !!disabledViews || campaign?.adtype === 'text';
  report.combinedViews = sumMetricSeries(report.views, report.basicviews).map(([time, value]) => [
    time,
    disabledViews ? 0 : value
  ]);
  report.combinedClicks = sumMetricSeries(report.clicks, report.basicclicks);
  report.trackedBasicViews = report.basicviews.map(([time, value]) => [time, disabledViews ? 0 : value]);
  report.totals.combinedViews = disabledViews ? 0 : report.totals.adview + report.totals.basicadview;
  report.totals.combinedClicks = report.totals.adclick + report.totals.basicadclick;
  Object.assign(report.totals, metricRates(report.totals, report.ctrUnavailable, report.ctrUnavailable));

  report.calculated.views = calculateRate(report.totals.adview, report.totals.ad, report.ctrUnavailable);
  report.calculated.clicks = report.totals.ctr;
  report.calculated.adviews = report.calculated.views;
  report.calculated.basicclicks = report.totals.basicCtr;
  report.calculated.basicviews = null; // Basic views cannot be attributed to recorded deliveries.
  report.calculated.combinedViews = null;
  report.calculated.combinedClicks = report.totals.combinedCtr;
  report.calculated.delivered = report.totals.ad;
  report.calculated.reach = report.totals.uniques;

  return report;
};
